import { Capacitor } from '@capacitor/core';
import { Plus, Pencil, Trash2, Calendar, Clock, Search, Filter, MoreVertical, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Target, Zap, ListTodo, ClipboardList } from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../../components/LoadingSpinner';
import {
  getCurrentTarget,
  getTargets,
  upsertTarget,
  getObjectives,
  createObjective,
  updateObjective,
  deleteObjective,
  getMilestonesByObjective,
  createMilestone,
  updateMilestone,
  deleteMilestone,
  getTasks,
  createTask,
  updateTask,
  deleteTask,
  syncTaskReminderNotifications,
} from '../../../../services/objectivesTasksService';
import type {
  UserTarget,
  CareerObjective,
  CareerMilestone,
  CareerTask,
  TaskStatus,
} from '../../../../types/objectivesTasks';
import { TASK_STATUS_ORDER } from '../../../../types/objectivesTasks';
import { getCurrentUserId } from '../../../../utils/authUtils';

const CARD_CLASS = 'vscode-bg-secondary rounded-xl border vscode-border overflow-hidden';

export default function ObjectivesTasksPage() {
  const { t } = useTranslation();
  const [userId, setUserId] = useState<string | null>(null);
  const [target, setTarget] = useState<UserTarget | null>(null);
  const [objectives, setObjectives] = useState<CareerObjective[]>([]);
  const [tasks, setTasks] = useState<CareerTask[]>([]);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState<string | null>(null);
  const [milestones, setMilestones] = useState<CareerMilestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const [targetForm, setTargetForm] = useState({ title: '', description: '', target_date: '' });
  const [objectiveModalOpen, setObjectiveModalOpen] = useState(false);
  const [editingObjective, setEditingObjective] = useState<CareerObjective | null>(null);
  const [objectiveForm, setObjectiveForm] = useState({
    title: '',
    description: '',
    category: 'custom' as const,
    priority: 'medium' as const,
    end_date: '',
  });
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<CareerTask | null>(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    objective_id: '',
    priority: 'medium' as const,
    due_date: '',
    estimated_time_minutes: '',
  });
  const [kanbanSearch, setKanbanSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [cardMenuOpen, setCardMenuOpen] = useState<string | null>(null);
  const [leftPanelExpanded, setLeftPanelExpanded] = useState(false);
  const [targetCardExpanded, setTargetCardExpanded] = useState(true);
  const [objectivesCardExpanded, setObjectivesCardExpanded] = useState(true);

  const load = useCallback(async () => {
    const uid = await getCurrentUserId();
    if (!uid) {
      setLoading(false);
      return;
    }
    setUserId(uid);
    try {
      const [targetRes, objectivesRes, tasksRes] = await Promise.all([
        getCurrentTarget(uid),
        getObjectives(uid),
        getTasks(uid),
      ]);
      setTarget(targetRes ?? null);
      setObjectives(objectivesRes ?? []);
      setTasks(tasksRes ?? []);
      syncTaskReminderNotifications(uid).catch(() => {});
    } catch (e) {
      console.error('[ObjectivesTasks] load error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedObjectiveId) {
      setMilestones([]);
      return;
    }
    getMilestonesByObjective(selectedObjectiveId).then(setMilestones).catch(console.error);
  }, [selectedObjectiveId]);

  const handleSaveTarget = async () => {
    if (!userId) return;
    try {
      const saved = await upsertTarget(userId, {
        title: targetForm.title || t('objectivesTasks.targetPlaceholder'),
        description: targetForm.description || null,
        target_date: targetForm.target_date || null,
      });
      setTarget(saved);
      setTargetModalOpen(false);
      setTargetForm({ title: saved.title, description: saved.description ?? '', target_date: saved.target_date ?? '' });
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveObjective = async () => {
    if (!userId) return;
    try {
      if (editingObjective) {
        const updated = await updateObjective(userId, editingObjective.id, {
          title: objectiveForm.title,
          description: objectiveForm.description || null,
          category: objectiveForm.category,
          priority: objectiveForm.priority,
          end_date: objectiveForm.end_date || null,
        });
        setObjectives((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      } else {
        const created = await createObjective(userId, {
          title: objectiveForm.title,
          description: objectiveForm.description || null,
          category: objectiveForm.category,
          priority: objectiveForm.priority,
          end_date: objectiveForm.end_date || null,
        });
        setObjectives((prev) => [created, ...prev]);
      }
      setObjectiveModalOpen(false);
      setEditingObjective(null);
      setObjectiveForm({ title: '', description: '', category: 'custom', priority: 'medium', end_date: '' });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteObjective = async (obj: CareerObjective) => {
    if (!userId || !window.confirm(t('objectivesTasks.confirmDeleteObjective'))) return;
    try {
      await deleteObjective(userId, obj.id);
      setObjectives((prev) => prev.filter((o) => o.id !== obj.id));
      if (selectedObjectiveId === obj.id) setSelectedObjectiveId(null);
    } catch (e) {
      console.error(e);
    }
  };

  const handleSaveTask = async () => {
    if (!userId) return;
    try {
      if (editingTask) {
        const updated = await updateTask(userId, editingTask.id, {
          title: taskForm.title,
          description: taskForm.description || null,
          objective_id: taskForm.objective_id || null,
          priority: taskForm.priority,
          due_date: taskForm.due_date || null,
          estimated_time_minutes: taskForm.estimated_time_minutes ? parseInt(taskForm.estimated_time_minutes, 10) : null,
        });
        setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      } else {
        const created = await createTask(userId, {
          title: taskForm.title,
          description: taskForm.description || null,
          objective_id: taskForm.objective_id || null,
          priority: taskForm.priority,
          due_date: taskForm.due_date || null,
          estimated_time_minutes: taskForm.estimated_time_minutes ? parseInt(taskForm.estimated_time_minutes, 10) : null,
        });
        setTasks((prev) => [created, ...prev]);
      }
      setTaskModalOpen(false);
      setEditingTask(null);
      setTaskForm({
        title: '',
        description: '',
        objective_id: selectedObjectiveId || '',
        priority: 'medium',
        due_date: '',
        estimated_time_minutes: '',
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteTask = async (task: CareerTask) => {
    if (!userId || !window.confirm(t('objectivesTasks.confirmDeleteTask'))) return;
    try {
      await deleteTask(userId, task.id);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    } catch (e) {
      console.error(e);
    }
  };

  const handleMoveTask = async (task: CareerTask, newStatus: TaskStatus) => {
    if (!userId || task.status === newStatus) return;
    try {
      const updated = await updateTask(userId, task.id, { status: newStatus });
      setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    } catch (e) {
      console.error(e);
    }
  };

  const searchLower = kanbanSearch.trim().toLowerCase();
  const filteredTasks = searchLower
    ? tasks.filter(
        (t) =>
          t.title?.toLowerCase().includes(searchLower) ||
          t.description?.toLowerCase().includes(searchLower) ||
          objectives.some((o) => o.id === t.objective_id && o.title?.toLowerCase().includes(searchLower))
      )
    : tasks;
  const tasksByStatus = TASK_STATUS_ORDER.reduce(
    (acc, status) => {
      acc[status] = filteredTasks.filter((t) => t.status === status);
      return acc;
    },
    {} as Record<TaskStatus, CareerTask[]>
  );

  const COLUMN_BAR_COLORS: Record<TaskStatus, string> = {
    backlog: 'var(--vscode-text-tertiary)',
    planned: 'var(--vscode-info)',
    in_progress: 'var(--vscode-warning)',
    blocked: 'var(--vscode-error)',
    completed: 'var(--vscode-success)',
    abandoned: 'var(--vscode-text-disabled)',
  };
  /** Column background — resolved from CSS variables so they adapt to light/dark theme */
  const COLUMN_BG_COLORS: Record<TaskStatus, string> = {
    backlog    : 'var(--kanban-bg-backlog,     rgba(113,113,122,0.08))',
    planned    : 'var(--kanban-bg-planned,     rgba(59,130,246,0.10))',
    in_progress: 'var(--kanban-bg-in-progress, rgba(249,115,22,0.10))',
    blocked    : 'var(--kanban-bg-blocked,     rgba(239,68,68,0.10))',
    completed  : 'var(--kanban-bg-completed,   rgba(34,197,94,0.10))',
    abandoned  : 'var(--kanban-bg-abandoned,   rgba(113,113,122,0.05))',
  };
  const categoryColors: Record<string, string> = {
    skill: 'var(--vscode-success)',
    resume: 'var(--vscode-info)',
    networking: '#8b5cf6',
    applications: 'var(--vscode-primary)',
    interview_prep: 'var(--vscode-warning)',
    mindset: '#ec4899',
    custom: 'var(--vscode-text-tertiary)',
  };
  const taskRef = (id: string) => '#' + id.slice(0, 6);

  const progressPercent =
    objectives.length > 0
      ? Math.round(
          (objectives.filter((o) => o.status === 'completed').length / objectives.length) * 100
        )
      : 0;

  if (loading) {
    return createPortal(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{ backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', background: 'var(--vscode-modal-overlay, rgba(0,0,0,0.4))' }}
      >
        <div
          className="rounded-2xl shadow-2xl w-full max-w-[340px] overflow-hidden flex flex-col"
          style={{ animation: 'fadeInUp 0.28s ease-out', background: 'var(--vscode-bg-elevated)', border: '1px solid var(--vscode-border-primary)' }}
        >
          {/* Gradient title bar */}
          <div
            className="flex items-center gap-3 px-5 py-4 shrink-0"
            style={{ background: 'var(--brand-cta-bg)' }}
          >
            <div className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center shadow-sm shrink-0">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-[0.15em] leading-none mb-1">
                My Activities
              </p>
              <h2 className="text-base font-bold text-white leading-tight">
                Loading your progress…
              </h2>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 py-5 flex flex-col items-center gap-4">
            {/* Spinner */}
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 rounded-full border-4 border-[#6C47FF]/15" />
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#6C47FF] animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Target className="w-6 h-6 text-[#6C47FF]" />
              </div>
            </div>

            {/* Status lines */}
            <div className="space-y-2 w-full">
              <div className="flex items-center gap-2.5">
                <div className="w-3.5 h-3.5 rounded-full border-2 border-[#6C47FF]/30 border-t-[#6C47FF] animate-spin shrink-0" />
                <span className="text-sm" style={{ color: 'var(--vscode-text-primary)' }}>Loading your objectives</span>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-3.5 h-3.5 rounded-full border-2 border-[#6C47FF]/30 border-t-[#6C47FF] animate-spin shrink-0" style={{ animationDelay: '150ms' }} />
                <span className="text-sm" style={{ color: 'var(--vscode-text-primary)' }}>Fetching tasks &amp; milestones</span>
              </div>
            </div>

            <p className="text-xs text-center leading-relaxed" style={{ color: 'var(--vscode-text-secondary)' }}>
              Track your career goals, milestones, and daily tasks all in one place.
            </p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div className={`ggj-light-palette p-2 sm:p-3 pb-4 flex-1 min-h-0 flex flex-col min-w-0 ${Capacitor.isNativePlatform() ? 'pt-2' : 'pt-8 sm:pt-10'}`}>
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-3 flex-1 min-h-0 w-full min-w-0">
        {/* Left: strips when cards collapsed so Kanban gets full view; narrow when both collapsed */}
        <aside
          className={
            'w-full shrink-0 flex flex-col gap-0 lg:gap-4 overflow-hidden lg:overflow-y-auto lg:min-h-0 min-h-0 max-h-[50vh] lg:max-h-none ' +
            (!targetCardExpanded && !objectivesCardExpanded ? 'lg:w-40' : 'lg:w-80')
          }
        >
          {/* Mobile/tablet: single tap-to-expand bar when collapsed */}
          <button
            type="button"
            onClick={() => setLeftPanelExpanded((e) => !e)}
            className="lg:hidden flex-shrink-0 flex items-center justify-between gap-2 w-full min-h-[52px] px-4 py-3 rounded-xl border vscode-border vscode-bg-secondary vscode-text-primary text-left"
          >
            <span className="truncate font-semibold">
              {target?.title || t('objectivesTasks.title')}
            </span>
            <span className="shrink-0 flex items-center gap-2">
              <span className="text-xs vscode-text-tertiary">{progressPercent}%</span>
              {leftPanelExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </span>
          </button>
          {/* Expandable content: visible when leftPanelExpanded (mobile) or always on lg; compact gap when both cards strips */}
          <div
            className={
              `flex flex-col overflow-y-auto min-h-0 flex-1 ${leftPanelExpanded ? 'max-h-[60vh]' : 'hidden'} lg:!flex lg:max-h-none mt-3 lg:mt-0 ` +
              (!targetCardExpanded && !objectivesCardExpanded ? 'gap-2' : 'gap-4')
            }
          >
          {!Capacitor.isNativePlatform() && (
          <div className="flex-shrink-0 mb-3 md:mb-4 hidden lg:block">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold ggj-page-title flex items-center gap-2 truncate min-w-0">
              <Target className="w-6 h-6 sm:w-7 sm:h-7 ggj-page-title-icon shrink-0" />
              <span>{t('objectivesTasks.title')}</span>
            </h1>
            <p className="text-xs sm:text-sm vscode-text-secondary mt-0.5 md:mt-1 line-clamp-2">
              {t('objectivesTasks.intent')}
            </p>
          </div>
          )}

          {/* Target — always 50%; when collapsed, entire card squeezes into strip at top of its half */}
          <section className={CARD_CLASS + ' flex flex-col min-h-0 flex-1'}>
            <div
              className={
                'flex items-center justify-between gap-2 shrink-0 w-full ' +
                (targetCardExpanded ? 'p-4 border-b vscode-border' : 'py-1.5 px-2')
              }
            >
              <h2 className="vscode-text-base vscode-font-semibold vscode-text-primary truncate min-w-0">
                {t('objectivesTasks.target')}
              </h2>
              <button
                type="button"
                onClick={() => setTargetCardExpanded((e) => !e)}
                className="shrink-0 p-1 rounded vscode-text-tertiary hover:vscode-text-primary"
                aria-label={targetCardExpanded ? t('objectivesTasks.collapse') : t('objectivesTasks.expand')}
              >
                {targetCardExpanded ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </button>
            </div>
            {targetCardExpanded && (
              <div className="p-4 flex flex-col gap-3 min-h-0 overflow-y-auto flex-1">
                <div className="min-w-0">
                  {target ? (
                    <>
                      <p className="vscode-text-xs vscode-text-tertiary uppercase tracking-wide mb-1">
                        {t('objectivesTasks.currentTarget')}
                      </p>
                      <p className="vscode-text-base vscode-font-medium vscode-text-primary break-words">
                        {target.title}
                      </p>
                      {target.target_date && (
                        <p className="vscode-text-sm vscode-text-tertiary mt-1">
                          <Calendar className="w-3.5 h-3.5 inline mr-1" />
                          {target.target_date}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="vscode-text-sm vscode-text-tertiary">{t('objectivesTasks.noTarget')}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full vscode-bg-primary/20 min-w-0">
                    <div
                      className="h-full rounded-full bg-[var(--vscode-primary)] transition-all"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (target) {
                        setTargetForm({
                          title: target.title,
                          description: target.description ?? '',
                          target_date: target.target_date ?? '',
                        });
                      } else {
                        setTargetForm({ title: '', description: '', target_date: '' });
                      }
                      setTargetModalOpen(true);
                    }}
                    className="p-2 rounded-lg vscode-bg-primary/10 hover:bg-[var(--vscode-primary)]/20 vscode-text-primary text-sm flex items-center gap-1 shrink-0"
                  >
                    <Pencil className="w-4 h-4" />
                    {target ? t('objectivesTasks.editTarget') : t('objectivesTasks.setTarget')}
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Objectives — always 50%; when collapsed, entire card squeezes into strip at top of its half */}
          <section className={CARD_CLASS + ' flex flex-col min-h-0 flex-1'}>
            <div
              className={
                'flex items-center justify-between gap-2 shrink-0 ' +
                (objectivesCardExpanded ? 'p-4 border-b vscode-border' : 'py-1.5 px-2')
              }
            >
              <h2 className="vscode-text-base vscode-font-semibold vscode-text-primary truncate min-w-0">
                {t('objectivesTasks.objectives')}
              </h2>
              <button
                type="button"
                onClick={() => setObjectivesCardExpanded((e) => !e)}
                className="shrink-0 p-1 rounded vscode-text-tertiary hover:vscode-text-primary"
                aria-label={objectivesCardExpanded ? t('objectivesTasks.collapse') : t('objectivesTasks.expand')}
              >
                {objectivesCardExpanded ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </button>
            </div>
            {objectivesCardExpanded && (
            <div className="p-4 overflow-y-auto min-h-0 flex-1 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0" />
                <button
                  type="button"
                  onClick={() => {
                    setEditingObjective(null);
                    setObjectiveForm({
                      title: '',
                      description: '',
                      category: 'custom',
                      priority: 'medium',
                      end_date: '',
                    });
                    setObjectiveModalOpen(true);
                  }}
                  className="p-2 rounded-lg vscode-bg-primary/10 hover:bg-[var(--vscode-primary)]/20 vscode-text-primary text-sm flex items-center gap-1 shrink-0"
                >
                  <Plus className="w-4 h-4" />
                  {t('objectivesTasks.addObjective')}
                </button>
              </div>
              {objectives.length === 0 ? (
                <p className="vscode-text-sm vscode-text-tertiary">{t('objectivesTasks.noObjectives')}</p>
              ) : (
                <div className="space-y-2">
                  {objectives.map((obj) => (
                    <div
                      key={obj.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border vscode-border cursor-pointer ${
                        selectedObjectiveId === obj.id
                          ? 'border-[var(--vscode-primary)] bg-[var(--vscode-primary)]/10'
                          : 'vscode-bg-primary/5 hover:vscode-bg-hover'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedObjectiveId(selectedObjectiveId === obj.id ? null : obj.id)}
                        className="text-left flex-1 min-w-0"
                      >
                        <span className="vscode-text-sm vscode-font-medium vscode-text-primary block truncate">
                          {obj.title}
                        </span>
                        <span className="vscode-text-xs vscode-text-tertiary">
                          {t(`objectivesTasks.categories.${obj.category}`)} · {t(`objectivesTasks.objectiveStatus.${obj.status}`)}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingObjective(obj);
                          setObjectiveForm({
                            title: obj.title,
                            description: obj.description ?? '',
                            category: obj.category,
                            priority: obj.priority,
                            end_date: obj.end_date ?? '',
                          });
                          setObjectiveModalOpen(true);
                        }}
                        className="p-1 rounded vscode-text-tertiary hover:vscode-text-primary shrink-0"
                        aria-label={t('objectivesTasks.editObjective')}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteObjective(obj);
                        }}
                        className="p-1 rounded vscode-text-tertiary hover:text-[var(--vscode-error)] shrink-0"
                        aria-label={t('objectivesTasks.delete')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}
          </section>

          {/* Milestones (when objective selected) */}
          {selectedObjectiveId && (
            <section className={CARD_CLASS + ' flex flex-col min-h-0 shrink-0'}>
              <div className="p-4 border-b vscode-border flex items-center justify-between shrink-0">
                <h2 className="vscode-text-sm vscode-font-semibold vscode-text-primary">
                  {t('objectivesTasks.milestones')}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    const title = window.prompt(t('objectivesTasks.addMilestone'));
                    if (title && userId) {
                      createMilestone(selectedObjectiveId, { title })
                        .then((m) => setMilestones((prev) => [...prev, m]))
                        .catch(console.error);
                    }
                  }}
                  className="text-sm text-[var(--vscode-primary)] hover:underline"
                >
                  {t('objectivesTasks.addMilestone')}
                </button>
              </div>
              <div className="p-4 overflow-y-auto min-h-0">
                {milestones.length === 0 ? (
                  <p className="vscode-text-sm vscode-text-tertiary">{t('objectivesTasks.noMilestones')}</p>
                ) : (
                  <ul className="space-y-2">
                    {milestones.map((m) => (
                      <li
                        key={m.id}
                        className="flex items-center justify-between py-2 px-3 rounded-lg vscode-bg-primary/5 border vscode-border"
                      >
                        <span className="vscode-text-sm vscode-text-primary truncate min-w-0">{m.title}</span>
                        <span className="vscode-text-xs vscode-text-tertiary shrink-0 ml-2">
                          {t(`objectivesTasks.milestoneStatus.${m.status}`)} · {m.completion_percentage}%
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}
          </div>
        </aside>

        {/* Right: Kanban full view — mobile: stacked columns + vertical scroll; desktop: horizontal scroll */}
        <main className="flex-1 min-w-0 flex flex-col min-h-0 mt-5 sm:mt-6 lg:mt-8">
      <section className={CARD_CLASS + ' flex flex-col flex-1 min-h-0 overflow-hidden min-w-0'}>
        {/* Toolbar: compact, two rows on mobile */}
        <div className="px-2 sm:px-3 py-2 border-b vscode-border shrink-0 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="vscode-text-base vscode-font-semibold vscode-text-primary truncate min-w-0">
              {t('objectivesTasks.kanban.boardTitle')}
            </h2>
            <button
              type="button"
              onClick={() => {
                setEditingTask(null);
                setTaskForm({
                  title: '',
                  description: '',
                  objective_id: selectedObjectiveId || '',
                  priority: 'medium',
                  due_date: '',
                  estimated_time_minutes: '',
                });
                setTaskModalOpen(true);
              }}
              className="flex items-center gap-1 px-2.5 py-2 min-h-[36px] rounded-lg vscode-bg-primary/10 hover:bg-[var(--vscode-primary)]/20 vscode-text-primary text-sm font-medium shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{t('objectivesTasks.addTask')}</span>
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1.5 rounded border vscode-border vscode-bg-tertiary/30">
              <Search className="w-3.5 h-3.5 vscode-text-tertiary flex-shrink-0" />
              <input
                type="text"
                value={kanbanSearch}
                onChange={(e) => setKanbanSearch(e.target.value)}
                placeholder={t('objectivesTasks.kanban.searchPlaceholder')}
                className="flex-1 min-w-0 vscode-bg-transparent vscode-text-primary text-sm outline-none"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className="flex items-center justify-center gap-1 px-2 py-1.5 min-h-[36px] rounded border vscode-border vscode-text-secondary text-xs shrink-0"
            >
              <Filter className="w-3.5 h-3.5" />
              {t('objectivesTasks.kanban.filters')}
            </button>
          </div>
        </div>
        {showFilters && selectedObjectiveId && (
          <div className="px-2 sm:px-3 py-1.5 border-b vscode-border vscode-text-secondary text-xs">
            {t('objectivesTasks.kanban.filterByObjective')}: {objectives.find((o) => o.id === selectedObjectiveId)?.title ?? ''}
          </div>
        )}
        {/* Mobile: vertical stack; Desktop: horizontal row — visible scrollbar for Kanban horizontal scroll */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden md:overflow-x-auto md:overflow-y-hidden vscode-scrollbar">
          <div className="flex flex-col gap-2 p-1.5 sm:p-2 md:flex-row md:gap-1 md:min-w-max md:h-full md:p-2">
            {TASK_STATUS_ORDER.map((status) => (
              <div
                key={status}
                className="w-full min-w-0 rounded-md border vscode-border flex flex-col min-h-0 md:w-52 md:shrink-0 md:max-w-[200px] lg:w-56 lg:max-w-[220px]"
                style={{ backgroundColor: COLUMN_BG_COLORS[status] }}
              >
                <div
                  className="h-0.5 shrink-0 rounded-t-md"
                  style={{ backgroundColor: COLUMN_BAR_COLORS[status] }}
                />
                <div className="px-2 py-1.5 border-b vscode-border flex items-center justify-between gap-1">
                  <span className="vscode-text-primary font-medium text-xs uppercase tracking-wide truncate min-w-0">
                    {t(`objectivesTasks.kanban.${status}`)}
                  </span>
                  <div className="flex items-center gap-0.5 shrink-0">
                    <span className="vscode-text-tertiary text-[10px] tabular-nums">
                      {tasksByStatus[status].length}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingTask(null);
                        setTaskForm({
                          title: '',
                          description: '',
                          objective_id: selectedObjectiveId || '',
                          priority: 'medium',
                          due_date: '',
                          estimated_time_minutes: '',
                        });
                        setTaskModalOpen(true);
                      }}
                      className="min-h-[32px] min-w-[32px] flex items-center justify-center p-1 rounded vscode-text-tertiary hover:vscode-text-primary"
                      title={t('objectivesTasks.addTask')}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="p-1.5 min-h-0 overflow-y-auto space-y-1.5 flex-1">
                  {tasksByStatus[status].map((task) => {
                    const obj = objectives.find((o) => o.id === task.objective_id);
                    const dotColor = obj ? (categoryColors[obj.category] ?? categoryColors.custom) : categoryColors.custom;
                    const isMenuOpen = cardMenuOpen === task.id;
                    return (
                      <div
                        key={task.id}
                        className="p-2 rounded-md border vscode-border vscode-bg-secondary hover:vscode-bg-hover group relative"
                      >
                        <div className="absolute top-1.5 left-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: dotColor }} />
                        {obj && (
                          <div className="vscode-text-tertiary text-[10px] truncate pl-3.5 mb-0.5" title={obj.title}>
                            [{obj.title}]
                          </div>
                        )}
                        <div className="pl-3.5 flex items-start justify-between gap-1">
                          <span className="vscode-text-tertiary text-[10px] font-mono shrink-0">{taskRef(task.id)}</span>
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setCardMenuOpen(isMenuOpen ? null : task.id)}
                              className="p-0.5 rounded vscode-text-tertiary hover:vscode-text-primary opacity-80 group-hover:opacity-100"
                            >
                              <MoreVertical className="w-3.5 h-3.5" />
                            </button>
                            {isMenuOpen && (
                              <>
                                <div className="fixed inset-0 z-10" onClick={() => setCardMenuOpen(null)} />
                                <div className="absolute right-0 top-full mt-0.5 z-20 min-w-[140px] py-0.5 rounded border vscode-border vscode-bg-secondary shadow-lg text-xs">
                                  <button
                                    type="button"
                                    className="w-full text-left px-2.5 py-1.5 vscode-text-primary hover:vscode-bg-tertiary/50 flex items-center gap-1.5"
                                    onClick={() => {
                                      setEditingTask(task);
                                      setTaskForm({
                                        title: task.title,
                                        description: task.description ?? '',
                                        objective_id: task.objective_id ?? '',
                                        priority: task.priority,
                                        due_date: task.due_date ?? '',
                                        estimated_time_minutes: task.estimated_time_minutes?.toString() ?? '',
                                      });
                                      setTaskModalOpen(true);
                                      setCardMenuOpen(null);
                                    }}
                                  >
                                    <Pencil className="w-3 h-3" />
                                    {t('objectivesTasks.editTask')}
                                  </button>
                                  {TASK_STATUS_ORDER.filter((s) => s !== task.status).map((s) => (
                                    <button
                                      key={s}
                                      type="button"
                                      className="w-full text-left px-2.5 py-1.5 vscode-text-primary hover:vscode-bg-tertiary/50"
                                      onClick={() => {
                                        handleMoveTask(task, s);
                                        setCardMenuOpen(null);
                                      }}
                                    >
                                      → {t(`objectivesTasks.kanban.${s}`)}
                                    </button>
                                  ))}
                                  <button
                                    type="button"
                                    className="w-full text-left px-2.5 py-1.5 hover:text-[var(--vscode-error)] hover:vscode-bg-tertiary/50 flex items-center gap-1.5"
                                    onClick={() => {
                                      handleDeleteTask(task);
                                      setCardMenuOpen(null);
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    {t('objectivesTasks.deleteTask')}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="vscode-text-xs vscode-font-medium vscode-text-primary pl-3.5 pr-8 break-words leading-snug">
                          {task.title}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-1 pl-3.5 vscode-text-tertiary text-[10px]">
                          {task.due_date && (
                            <span className="flex items-center gap-0.5">
                              <Calendar className="w-2.5 h-2.5" />
                              {task.due_date}
                            </span>
                          )}
                          {task.estimated_time_minutes != null && (
                            <span className="flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5" />
                              {task.estimated_time_minutes} {t('objectivesTasks.minutesShort')}
                            </span>
                          )}
                        </div>
                        <div className="pl-3.5 mt-0.5 vscode-text-tertiary text-[10px]">
                          {t('objectivesTasks.notAssigned')}
                        </div>
                      </div>
                    );
                  })}
                  {tasksByStatus[status].length === 0 && (
                    <p className="vscode-text-tertiary text-[11px] text-center py-2 px-1">
                      {t('objectivesTasks.noTasks')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
        </main>
      </div>

      {/* Target modal — full-screen on mobile */}
      {targetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50" onClick={() => setTargetModalOpen(false)}>
          <div
            className="vscode-bg-secondary border-0 sm:border vscode-border rounded-t-2xl sm:rounded-xl w-full max-h-[95dvh] sm:max-h-[90vh] overflow-hidden sm:max-w-md shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 px-4 sm:px-6 py-3 rounded-t-2xl sm:rounded-t-xl border-b flex items-center gap-2" style={{ backgroundColor: 'var(--ggj-header-bg)', borderColor: 'var(--ggj-header-bg)' }}>
              <Target className="w-5 h-5 shrink-0 text-[var(--ggj-header-text,#fff)]" />
              <h3 className="vscode-text-lg vscode-font-semibold text-[var(--ggj-header-text,#fff)]">
                {target ? t('objectivesTasks.editTarget') : t('objectivesTasks.setTarget')}
              </h3>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto min-h-0">
            <p className="vscode-text-tertiary text-sm mb-3">{t('objectivesTasks.targetModalHelp')}</p>
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.targetTitleLabel')}</label>
            <input
              type="text"
              value={targetForm.title}
              onChange={(e) => setTargetForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={t('objectivesTasks.targetPlaceholder')}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3"
            />
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.targetDescriptionLabel')}</label>
            <textarea
              value={targetForm.description}
              onChange={(e) => setTargetForm((f) => ({ ...f, description: e.target.value }))}
              placeholder={t('objectivesTasks.taskDescription')}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3 resize-none h-20"
            />
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.targetDateLabel')}</label>
            <input
              type="date"
              value={targetForm.target_date}
              onChange={(e) => setTargetForm((f) => ({ ...f, target_date: e.target.value }))}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-4"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => setTargetModalOpen(false)}
                className="min-h-[44px] px-4 py-2.5 rounded-xl border vscode-border vscode-text-secondary"
              >
                {t('objectivesTasks.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveTarget}
                className="min-h-[44px] px-4 py-2.5 rounded-xl bg-[var(--vscode-primary)] text-[var(--vscode-primary-text,#fff)]"
              >
                {t('objectivesTasks.save')}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Objective modal — full-screen on mobile */}
      {objectiveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50" onClick={() => setObjectiveModalOpen(false)}>
          <div
            className="vscode-bg-secondary border-0 sm:border vscode-border rounded-t-2xl sm:rounded-xl w-full max-h-[95dvh] sm:max-h-[90vh] overflow-hidden sm:max-w-md shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 px-4 sm:px-6 py-3 rounded-t-2xl sm:rounded-t-xl border-b flex items-center gap-2" style={{ backgroundColor: 'var(--ggj-header-bg)', borderColor: 'var(--ggj-header-bg)' }}>
              <ListTodo className="w-5 h-5 shrink-0 text-[var(--ggj-header-text,#fff)]" />
              <h3 className="vscode-text-lg vscode-font-semibold text-[var(--ggj-header-text,#fff)]">
                {editingObjective ? t('objectivesTasks.editObjective') : t('objectivesTasks.addObjective')}
              </h3>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto min-h-0">
            <p className="vscode-text-tertiary text-sm mb-3">{t('objectivesTasks.objectiveModalHelp')}</p>
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.objectiveTitleLabel')}</label>
            <input
              type="text"
              value={objectiveForm.title}
              onChange={(e) => setObjectiveForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={t('objectivesTasks.taskTitle')}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3"
            />
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.objectiveDescriptionLabel')}</label>
            <textarea
              value={objectiveForm.description}
              onChange={(e) => setObjectiveForm((f) => ({ ...f, description: e.target.value }))}
              placeholder={t('objectivesTasks.taskDescription')}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3 resize-none h-20"
            />
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.objectiveCategoryLabel')}</label>
            <select
              value={objectiveForm.category}
              onChange={(e) => setObjectiveForm((f) => ({ ...f, category: e.target.value as CareerObjective['category'] }))}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3"
            >
              {(['skill', 'resume', 'networking', 'applications', 'interview_prep', 'mindset', 'custom'] as const).map(
                (c) => (
                  <option key={c} value={c}>
                    {t(`objectivesTasks.categories.${c}`)}
                  </option>
                )
              )}
            </select>
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.objectivePriorityLabel')}</label>
            <select
              value={objectiveForm.priority}
              onChange={(e) => setObjectiveForm((f) => ({ ...f, priority: e.target.value as 'low' | 'medium' | 'high' }))}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3"
            >
              <option value="low">{t('objectivesTasks.priorities.low')}</option>
              <option value="medium">{t('objectivesTasks.priorities.medium')}</option>
              <option value="high">{t('objectivesTasks.priorities.high')}</option>
            </select>
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.objectiveEndDateLabel')}</label>
            <input
              type="date"
              value={objectiveForm.end_date}
              onChange={(e) => setObjectiveForm((f) => ({ ...f, end_date: e.target.value }))}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-4"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => setObjectiveModalOpen(false)}
                className="min-h-[44px] px-4 py-2.5 rounded-xl border vscode-border vscode-text-secondary"
              >
                {t('objectivesTasks.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveObjective}
                className="min-h-[44px] px-4 py-2.5 rounded-xl bg-[var(--vscode-primary)] text-[var(--vscode-primary-text,#fff)]"
              >
                {t('objectivesTasks.save')}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Task modal — full-screen on mobile */}
      {taskModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50" onClick={() => setTaskModalOpen(false)}>
          <div
            className="vscode-bg-secondary border-0 sm:border vscode-border rounded-t-2xl sm:rounded-xl w-full max-h-[95dvh] sm:max-h-[90vh] overflow-hidden sm:max-w-md shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 px-4 sm:px-6 py-3 rounded-t-2xl sm:rounded-t-xl border-b flex items-center gap-2" style={{ backgroundColor: 'var(--ggj-header-bg)', borderColor: 'var(--ggj-header-bg)' }}>
              <ClipboardList className="w-5 h-5 shrink-0 text-[var(--ggj-header-text,#fff)]" />
              <h3 className="vscode-text-lg vscode-font-semibold text-[var(--ggj-header-text,#fff)]">
                {editingTask ? t('objectivesTasks.editTask') : t('objectivesTasks.addTask')}
              </h3>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto min-h-0">
            <p className="vscode-text-tertiary text-sm mb-3">{t('objectivesTasks.taskModalHelp')}</p>
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.taskTitle')}</label>
            <input
              type="text"
              value={taskForm.title}
              onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={t('objectivesTasks.taskTitle')}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3"
            />
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.taskDescription')}</label>
            <textarea
              value={taskForm.description}
              onChange={(e) => setTaskForm((f) => ({ ...f, description: e.target.value }))}
              placeholder={t('objectivesTasks.taskDescription')}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3 resize-none h-20"
            />
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.taskObjectiveLabel')}</label>
            <select
              value={taskForm.objective_id}
              onChange={(e) => setTaskForm((f) => ({ ...f, objective_id: e.target.value }))}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3"
            >
              <option value="">{t('objectivesTasks.allObjectives')}</option>
              {objectives.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.title}
                </option>
              ))}
            </select>
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.priority')}</label>
            <select
              value={taskForm.priority}
              onChange={(e) => setTaskForm((f) => ({ ...f, priority: e.target.value as 'low' | 'medium' | 'high' }))}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3"
            >
              <option value="low">{t('objectivesTasks.priorities.low')}</option>
              <option value="medium">{t('objectivesTasks.priorities.medium')}</option>
              <option value="high">{t('objectivesTasks.priorities.high')}</option>
            </select>
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.taskDueDateLabel')}</label>
            <input
              type="date"
              value={taskForm.due_date}
              onChange={(e) => setTaskForm((f) => ({ ...f, due_date: e.target.value }))}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-3"
            />
            <label className="block vscode-text-secondary text-sm font-medium mb-1">{t('objectivesTasks.taskEstimatedTimeLabel')}</label>
            <input
              type="number"
              min="0"
              value={taskForm.estimated_time_minutes}
              onChange={(e) => setTaskForm((f) => ({ ...f, estimated_time_minutes: e.target.value }))}
              placeholder={t('objectivesTasks.estimatedTime')}
              className="w-full px-3 py-2 vscode-input rounded-lg vscode-text-primary mb-4"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => setTaskModalOpen(false)}
                className="min-h-[44px] px-4 py-2.5 rounded-xl border vscode-border vscode-text-secondary"
              >
                {t('objectivesTasks.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSaveTask}
                className="min-h-[44px] px-4 py-2.5 rounded-xl bg-[var(--vscode-primary)] text-[var(--vscode-primary-text,#fff)]"
              >
                {t('objectivesTasks.save')}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
